import { useEffect, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { forgetTrustTokens, TRUST_CHANGED_EVENT, trustedUntil } from "../../auth/trustedBrowser";
import {
  contactErrorText,
  finishProviderLink,
  forgetTrustedBrowsers,
  getContactDetails,
  removeProviderLink,
  startProviderLink,
} from "../../api/account";
import { maskMobile, testCodeAccessToken } from "../../api/login";
import { sendAccountCode } from "../../api/password";
import { codeSentLine } from "../../components/CodeChannelChoice";

const LINK_TICKET_KEY = "ankhos.linkTicket";

function readStored(key: string): string {
  try {
    return window.sessionStorage.getItem(key) ?? "";
  } catch {
    return "";
  }
}

/**
 * Settings › Sign-in & security › Sign-in methods (owner 2026-10-25): link
 * Google to your own login, see what is linked, remove a link, and forget
 * the browsers you trusted.
 *
 * Linking: Link Google -> Google -> back here with ?link=ok&id=..&token=..
 * -> a fresh SMS code to your own mobile -> radaccess writes the link (and
 * emails the required notice to your verified email). The Google email need
 * not match your AnkhOS email.
 */
export function SignInMethodsCard() {
  const identity = useIdentity();
  const ns = identity.rootNamespace ?? identity.namespace ?? "";
  const qc = useQueryClient();
  const contact = useQuery({
    queryKey: ["contactDetails", ns],
    enabled: Boolean(identity.token && ns),
    retry: false,
    queryFn: ({ signal }) => getContactDetails(identity, ns, signal),
  });
  const reload = () => void qc.invalidateQueries({ queryKey: ["contactDetails", ns] });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ severity: "success" | "error" | "info"; text: string } | null>(null);
  // This browser's own trust, re-read whenever a card changes it.
  const [until, setUntil] = useState(() => trustedUntil(identity.subject));
  useEffect(() => {
    const read = () => setUntil(trustedUntil(identity.subject));
    read();
    window.addEventListener(TRUST_CHANGED_EVENT, read);
    return () => window.removeEventListener(TRUST_CHANGED_EVENT, read);
  }, [identity.subject]);

  // Coming back from the provider.
  const [returned, setReturned] = useState<{ intentId: string; intentToken: string; ticket: string } | null>(null);
  const [reqId, setReqId] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [code, setCode] = useState("");
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const result = p.get("link");
    if (!result) return;
    const ticket = readStored(LINK_TICKET_KEY);
    try {
      window.sessionStorage.removeItem(LINK_TICKET_KEY);
    } catch {
      // nothing stored
    }
    window.history.replaceState(null, "", window.location.pathname);
    const intentId = p.get("id") ?? "";
    const intentToken = p.get("token") ?? "";
    if (result !== "ok" || !intentId || !intentToken || !ticket) {
      setNotice({ severity: "error", text: "The Google sign-in did not finish. Try Link Google again." });
      return;
    }
    setReturned({ intentId, intentToken, ticket });
    setNotice({ severity: "info", text: "Google signed in. Confirm with a code sent to your mobile and email to finish linking." });
  }, []);

  async function run(action: () => Promise<void>) {
    if (busy) return;
    setBusy(true);
    try {
      await action();
    } catch (err) {
      setNotice({ severity: "error", text: contactErrorText(err) });
    } finally {
      setBusy(false);
    }
  }

  const link = (idpId: string) =>
    run(async () => {
      const s = await startProviderLink(identity, ns, idpId);
      try {
        window.sessionStorage.setItem(LINK_TICKET_KEY, s.ticket);
      } catch {
        // Without storage the return cannot be finished; say so rather than send them off.
        throw new Error("storage");
      }
      window.location.assign(s.authUrl);
    });

  const sendCode = () =>
    run(async () => {
      // Owner 2026-10-25: ONE code by SMS and to the verified email (SMS
      // alone when no email is verified -- radaccess decides).
      const c = await sendAccountCode(identity, ns, ["sms", "email"], "link_account");
      setReqId(c.requestId);
      setSentTo(codeSentLine(maskMobile(contact.data?.mobile ?? ""), c.sms, c.email));
      setCode("");
    });

  const confirm = (e: FormEvent) => {
    e.preventDefault();
    if (!returned || !contact.data) return;
    const mobile = contact.data.mobile;
    void run(async () => {
      try {
        await finishProviderLink(identity, ns, { ...returned, mobile, accessToken: testCodeAccessToken(reqId, code) });
      } finally {
        setCode("");
        setReqId("");
      }
      setReturned(null);
      setNotice({ severity: "success", text: "Google linked. Next time you can sign in with Google." });
      reload();
    });
  };

  const remove = (idpId: string, externalUserId: string) =>
    run(async () => {
      await removeProviderLink(identity, ns, idpId, externalUserId);
      setNotice({ severity: "success", text: "Link removed." });
      reload();
    });

  const forget = () =>
    run(async () => {
      await forgetTrustedBrowsers(identity, ns);
      forgetTrustTokens();
      setNotice({ severity: "success", text: "Every trusted browser is forgotten. The next sign-in asks the extra step again." });
    });

  const d = contact.data;
  const linkedIds = new Set(d?.links.map((l) => l.idpId) ?? []);
  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 2 }} data-testid="signin-methods">
      <Stack spacing={2}>
        <Typography variant="h6" component="h2">
          Sign-in methods
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Link Google to sign in with one click. Your Google email does not have to match your AnkhOS email.
        </Typography>
        {notice && <Alert severity={notice.severity}>{notice.text}</Alert>}
        {d && d.twoFactorRequired && (
          <Alert severity="info">
            Your role needs a second step at every sign-in: the authenticator app, a code, or your password.
          </Alert>
        )}

        {returned && d && (
          <form onSubmit={confirm} noValidate>
            <Stack spacing={1.5}>
              {reqId && <Typography variant="body2">{sentTo}</Typography>}
              {reqId && (
                <TextField
                  label="6-digit code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  autoComplete="one-time-code"
                  size="small"
                  slotProps={{ htmlInput: { inputMode: "numeric", "data-testid": "link-code" } }}
                  sx={{ maxWidth: 200 }}
                />
              )}
              <Stack direction="row" spacing={1}>
                {reqId ? (
                  <Button type="submit" variant="contained" disabled={busy || code.length !== 6}>
                    Link Google
                  </Button>
                ) : (
                  <Button variant="contained" onClick={() => void sendCode()} disabled={busy || !d.mobile}>
                    {d.mobile ? "Send code" : "Add a mobile above first"}
                  </Button>
                )}
                <Button onClick={() => { setReturned(null); setNotice(null); }} disabled={busy}>
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </form>
        )}

        {d && (
          <Box>
            {d.links.map((l) => (
              <Box key={l.idpId + l.externalUserId} sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
                <Chip size="small" color="success" label={l.provider || "Linked"} />
                <Typography variant="body2" data-testid="linked-account">
                  {l.name || l.externalUserId}
                </Typography>
                <Button size="small" color="error" onClick={() => void remove(l.idpId, l.externalUserId)} disabled={busy}>
                  Remove
                </Button>
              </Box>
            ))}
            {d.providers
              .filter((p) => !linkedIds.has(p.id))
              .map((p) => (
                <Button key={p.id} variant="outlined" onClick={() => void link(p.id)} disabled={busy || Boolean(returned)} sx={{ mr: 1 }}>
                  Link {p.name || "account"}
                </Button>
              ))}
            {d.links.length === 0 && d.providers.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No other sign-in method can be linked right now.
              </Typography>
            )}
          </Box>
        )}

        <Box>
          <Typography variant="subtitle2">Trusted browsers</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Browsers where you chose "Trust this browser" skip the extra sign-in step for a while. Changing your password,
            your authenticator app, mobile or email forgets them all.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }} data-testid="this-browser-trust">
            {until > 0
              ? `This browser is trusted until ${new Date(until).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}: no extra step here until then.`
              : "This browser is not trusted: every sign-in here asks the extra step."}
          </Typography>
          <Button size="small" variant="outlined" onClick={() => void forget()} disabled={busy}>
            Forget all trusted browsers
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
}
